import { useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import { APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE, APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE, DOCUMENT_CONTENT_TYPE_PDF, DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE, DOCUMENT_TYPE_EDUCATION_CERTIFICATE } from '../../../../app/constants';
import { convertFileDataForUrl } from '../../../../app/documentUploadUtils';
import { DocumentViewerFileNameLink, DownloadFileNameLink } from '../../../../components/DocumentViewer/DocumentFileComponent';
import { LineSeperator } from '../../../../components/RegularComponents';
import { saveBusinessLicenceApplicationData } from '../../../../redux/slice/businessLicenceApplicationSlice';
import { addCurrentAgentSupportingDocument } from '../../../../redux/slice/additionalApplicationsSlice';
import { fetchDocumentData } from '../../../../redux/slice/supportingDocumentsSlice';
import { InformationDetailPanelSection } from '../InformationDetailPanelSection'
import DocumentViewer from '../../../../components/DocumentViewer/DocumentViewer';
import { CommonAttachmentsTab } from './CommonAttachmentsTab';
import { addAgencySupportingDocument, NOT_LICENCED } from '../../../../redux/slice/primaryAgencyApplicationSlice';
import { applicationTypeFromLicenceSubType, isRenewalTypeApplication } from '../../../../redux/slice/applicationsUtils';
import { humanReadableDate } from '../../../../app/utils';

export const BrokerEducationRequirementDetails = (props) => {
    const {
        appData,
        licenceApplication
    } = props

    const {
        licenceHistoryData,
        confirmEducationQualificationCompletion,
        hasRequiredEducationCertificate,
        hasRequiredEducationQualification,
        mraBrokerRegulatoryInfoDate,
        mraBrokerMpcCompletionDate,
        previouslyLicencedStatus
    } = appData

    const {
        licencingSubType,
        documents,
        cepRipExpiryDate
    } = licenceApplication

    const applicationType = applicationTypeFromLicenceSubType(licencingSubType);

    const dispatch = useDispatch()
    const {userAuth} = useStore().getState()

    const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
    const [documentViewerDocument, setDocumentViewerDocument] = useState(null);
    const [selectedFile, setSelectedFile] = useState(null);

    const q1 = "Has the applicant completed the education requirement?"
    const components = []

    const documentComponent = (d) => { 
        return (
            <>
                { d.documentContentType === DOCUMENT_CONTENT_TYPE_PDF ? 
                    <DocumentViewerFileNameLink
                        id={"bladmin-information-attachment-file-" + d.filename}
                        key={"bladmin-information-attachment-file-" + d.filename}
                        file={d} 
                        onClick={() => {
                            if(d.data) { 
                                let fileDataUrl = convertFileDataForUrl(d.data, d.documentContentType)
                                setDocumentViewerDocument(fileDataUrl)
                                setOpenDocumentViewer(true)
                                setSelectedFile(d)
                            } else { 
                                // download data and then view it.
                                if(d.guid) { 
                                    dispatch(fetchDocumentData(d.guid))
                                        .then(downloadFile => {
                                            if(downloadFile) {
                                                let fileDataUrl = convertFileDataForUrl(downloadFile.data, downloadFile.documentContentType)
                                                setDocumentViewerDocument(fileDataUrl)
                                                setOpenDocumentViewer(true)
                                                setSelectedFile(downloadFile)
                                            }
                                        })
                                        .catch((error) => {
                                            console.error(error)
                                        })
                                }
                            }
                        }}
                    /> : 
                    <DownloadFileNameLink
                        id={"bladmin-information-attachment-download-file-" + d.filename}
                        componentKey={"bladmin-information-attachment-download-file-" + d.filename}
                        file={d} 
                    />}
            </>
        )
    }

    const exemptText =
        previouslyLicencedStatus === NOT_LICENCED
            ? "This applicant is exempt because they have a current Associate Mortgage Broker Licence (or equivalent) from an exempt province."
            : "This applicant is exempt because they have a current Mortgage Broker Licence from an exempt province.";

    // if there is history data available, then this broker is exempted from the education requirements
    if(licenceHistoryData) {
        components.push(
            <div style={{marginTop: "1em"}}>
                <InformationDetailPanelSection 
                    header={q1} 
                    value={exemptText} />
            </div>
        )
    } else {
        if(hasRequiredEducationQualification) {
            components.push(
                <InformationDetailPanelSection 
                        header={q1} 
                        value={"Yes"}
                />
            )
        } else {
            components.push(
                <InformationDetailPanelSection 
                        header={q1} 
                        value={"No"}
                />
            )
        }
    
        components.push(<LineSeperator />)
    
        if(hasRequiredEducationCertificate) {
            documents.filter(d => d.documentType  === DOCUMENT_TYPE_EDUCATION_CERTIFICATE).forEach(file => {
                const uiComponent = (<span style={{cursor: "pointer"}}>{documentComponent(file)}</span>)
                components.push(
                    <InformationDetailPanelSection 
                        header={"Uploaded file"} 
                        value={uiComponent}
                    />
                )
            })
        } else {
            if(confirmEducationQualificationCompletion) {
                components.push(
                    <InformationDetailPanelSection 
                        header={"Certificate of completion"} 
                        value={"The applicant agrees to complete the course and the Principal broker will send the certificate of completion on their behalf within 1 year."}
                    />
                )
            }
        }
    }

    if(!isRenewalTypeApplication(licenceApplication)) {
        components.push(<LineSeperator />)

        components.push(<h2>Education continuation information</h2>)

        if(applicationType !== APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE) {

            //For Broker to Principal broker transfer
            if(mraBrokerRegulatoryInfoDate) {
                components.push(
                    <div style={{marginTop: "1em"}}>
                        <InformationDetailPanelSection 
                            header={"Regulatory Information Program completion date"} 
                            value={mraBrokerRegulatoryInfoDate ? humanReadableDate(mraBrokerRegulatoryInfoDate) : "---"}
                        />
                    </div>
                )
            }

            components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"Regulatory Information Program valid until date"} 
                        value={cepRipExpiryDate ? humanReadableDate(cepRipExpiryDate) : "---"}
                    />
                </div>
            )
        }

        // For Broker to Principal broker transfer
        if(mraBrokerMpcCompletionDate) {
             components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"MPC Continuing Education Program completion date"} 
                        value={mraBrokerMpcCompletionDate ? humanReadableDate(mraBrokerMpcCompletionDate) : "---"}
                    />
                </div>
            )

            components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"MPC Continuing Education Program valid until date"} 
                        value={cepRipExpiryDate ? humanReadableDate(cepRipExpiryDate) : "---"}
                    />
                </div>
            )

             const educationContinuingDocuments = documents?.filter(doc => 
                doc.documentType === DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE && doc.changeType !== "DELETE")
                .sort((f1,f2) => f2.createdDate - f1.createdDate)
            
            if(educationContinuingDocuments?.length > 0) {
                educationContinuingDocuments.forEach(file => {
                    const uiComponent = (<span style={{cursor: "pointer"}}>{documentComponent(file)}</span>)
                    components.push(
                        <div style={{marginTop: "1em"}}>
                            <InformationDetailPanelSection 
                                header={"Uploaded file"} 
                                value={uiComponent}
                            />
                        </div>
                    )
                })
            } else {
                components.push(
                    <div style={{marginTop: "1em"}}>
                        <InformationDetailPanelSection 
                            header={"Uploaded file"} 
                            value={"No Education continuing certificate uploaded."}
                        />
                    </div>
                )
            }
        }
        else {
            components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"Education Program valid until date"} 
                        value={cepRipExpiryDate ? humanReadableDate(cepRipExpiryDate) : "---"}
                    />
                </div>
            )
        }
    }
    else {

        components.push(<LineSeperator />)

        components.push(<h2>Education continuation information</h2>)

        if(applicationType !== APPLICATION_TYPE_MORTGAGE_ASSOCIATE_BROKER_LICENCE) {
            components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"Regulatory Information Program completion date"} 
                        value={mraBrokerRegulatoryInfoDate ? humanReadableDate(mraBrokerRegulatoryInfoDate) : "---"}
                    />
                </div>
            )

            components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"Regulatory Information Program valid until date"} 
                        value={cepRipExpiryDate ? humanReadableDate(cepRipExpiryDate) : "---"}
                    />
                </div>
            )
        }

        components.push(
            <div style={{marginTop: "1em"}}>
                <InformationDetailPanelSection 
                    header={"MPC Continuing Education Program completion date"} 
                    value={mraBrokerMpcCompletionDate ? humanReadableDate(mraBrokerMpcCompletionDate) : "---"}
                />
            </div>
        )

        components.push(
            <div style={{marginTop: "1em"}}>
                <InformationDetailPanelSection 
                    header={"MPC Continuing Education Program valid until date"} 
                    value={cepRipExpiryDate ? humanReadableDate(cepRipExpiryDate) : "---"}
                />
            </div>
        )

        const educationContinuingDocuments = documents?.filter(doc => 
            doc.documentType === DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE && doc.changeType !== "DELETE")
            .sort((f1,f2) => f2.createdDate - f1.createdDate)
        
        if(educationContinuingDocuments?.length > 0) {
            educationContinuingDocuments.forEach(file => {
                const uiComponent = (<span style={{cursor: "pointer"}}>{documentComponent(file)}</span>)
                components.push(
                    <div style={{marginTop: "1em"}}>
                        <InformationDetailPanelSection 
                            header={"Uploaded file"} 
                            value={uiComponent}
                        />
                    </div>
                )
            })
        } else {
            components.push(
                <div style={{marginTop: "1em"}}>
                    <InformationDetailPanelSection 
                        header={"Uploaded file"} 
                        value={"No Education continuing certificate uploaded."}
                    />
                </div>
            )
        }
        
    }

    components.push(<LineSeperator />)

    const  handleFileUpload = (fileObj) => {
        // upload the file
        /* */ 
        if(fileObj && Object.keys(fileObj).length > 0) {
            const obj = {...fileObj,
                createdDate: new Date().getTime(),
                createdBy : userAuth?.userData?.email ?? "",
                changeType: "SAVE"
            }
            const {applicationType, applicationMode} = props

            if(applicationType === APPLICATION_TYPE_MORTGAGE_BROKERAGE_LICENCE) {
                dispatch(addAgencySupportingDocument(obj))
            } else {
                dispatch(addCurrentAgentSupportingDocument(obj))
            }
            
            dispatch(
                saveBusinessLicenceApplicationData(applicationType, applicationMode)
            ).then(data => {
                    
            })
        }
    }

    const commonAttachmentControl = (
        <CommonAttachmentsTab 
            documentTypes={[DOCUMENT_TYPE_EDUCATION_CERTIFICATE, DOCUMENT_TYPE_CONTINUING_EDUCATION_CERTIFICATE]} 
            onFileUpload = {handleFileUpload}
            {...props} 
        />)

    components.push(commonAttachmentControl)

    return (
        <>
        <div style={{marginTop: "1em"}}>
            {components}
        </div>
        <DocumentViewer 
                fileData={documentViewerDocument}
                isOpen={openDocumentViewer}
                fileName={selectedFile?.filename}
                handleClose={() => setOpenDocumentViewer(false)}
            />
        </>
    )
}
